import { useCallback, useEffect, useState } from 'react';
import { ApiError, fetchCourse, type CourseView, type LessonCard as LessonCardData, type ModuleView } from '../api';
import { useSession, useTenant } from '../app';
import { CheckIcon, Kicker, Link, LockIcon, PlayIcon, ProgressBar, Thumb, TwoLineTitle } from '../components';
import { formatClock, formatDate, formatDurationBig, formatPrice, pad2, plural } from '../format';
import { navigate } from '../router';
import { LessonOverlay } from './lesson';

/** Die Kursübersicht (Konzept 4.3) – Bühne, Kennzahlen, Module mit Lektions-Kacheln; darüber die Lektion als Fenster. */
export function CoursePage({ slug, lessonNumber }: { slug: string; lessonNumber: number | null }) {
  const tenant = useTenant();
  const session = useSession();
  const [view, setView] = useState<CourseView | null | undefined>(undefined);

  const reload = useCallback(() => {
    fetchCourse(slug)
      .then((v) => {
        setView(v);
        document.title = `${v.course.title.replace('\n', ' ')} – ${tenant.name}`;
      })
      .catch((e: unknown) => setView(e instanceof ApiError && e.status === 404 ? null : null));
  }, [slug, tenant.name]);

  useEffect(reload, [reload]);

  if (view === undefined) return <main className="page" />;
  if (view === null) {
    return (
      <main className="page page--message">
        <h1>Diesen Kurs gibt es nicht</h1>
        <p className="muted">
          <Link href="/" className="link">
            Zur Bibliothek
          </Link>
        </p>
      </main>
    );
  }

  const locked = view.access === 'locked';
  const dur = formatDurationBig(view.stats.durationSeconds);
  const openLesson = lessonNumber !== null ? view.modules.flatMap((m) => m.lessons).find((l) => l.number === lessonNumber) : undefined;
  const firstLessonNumber = view.modules.flatMap((m) => m.lessons)[0]?.number ?? 1;

  return (
    <main className="page course">
      <section className="hero">
        <Thumb src={view.course.imageUrl} seed={7} className="hero__image" />
        <div className="hero__body">
          {view.course.kicker && <Kicker>{view.course.kicker}</Kicker>}
          <TwoLineTitle title={view.course.title} className="hero__title" />
          {view.course.description && <p className="hero__lead">{view.course.description}</p>}
          <div className="kpis">
            <div className="kpi">
              <span className="kpi__value">{view.stats.lessons}</span>
              <span className="kpi__label">{view.stats.lessons === 1 ? 'Lektion' : 'Lektionen'}</span>
            </div>
            <div className="kpi">
              <span className="kpi__value">{dur.value}</span>
              <span className="kpi__label">{dur.label}</span>
            </div>
            {view.stats.chapters > 0 && (
              <div className="kpi">
                <span className="kpi__value">{view.stats.chapters}</span>
                <span className="kpi__label">Sprungmarken</span>
              </div>
            )}
            {!locked && (
              <div className="kpi">
                <span className="kpi__value">{view.stats.percent} %</span>
                <span className="kpi__label">erledigt</span>
              </div>
            )}
          </div>
          <div className="hero__actions">
            {locked ? (
              <>
                {view.product ? (
                  <Link href={`/kaufen/${view.product.slug}`} className="button button--large">
                    {view.product.priceCents === 0 ? 'Kostenlos freischalten' : `Jetzt kaufen · ${formatPrice(view.product.priceCents, view.product.currency)}`}
                  </Link>
                ) : (
                  <span className="muted">Dieser Kurs ist derzeit nicht erhältlich.</span>
                )}
                {!session.loggedIn && (
                  <Link href="/anmelden" className="button button--secondary button--large">
                    Ich habe schon Zugang
                  </Link>
                )}
              </>
            ) : (
              <>
                {view.resume ? (
                  <Link href={`/kurs/${view.course.slug}/lektion/${view.resume.number}`} className="button button--large">
                    <PlayIcon /> Weiterlernen ab Lektion {pad2(view.resume.number)}
                    {view.resume.positionSeconds > 0 && <span className="button__sub">· {formatClock(view.resume.positionSeconds)}</span>}
                  </Link>
                ) : view.stats.percent >= 100 ? (
                  <Link href={`/kurs/${view.course.slug}/lektion/${firstLessonNumber}`} className="button button--large">
                    Noch einmal von vorn
                  </Link>
                ) : (
                  <Link href={`/kurs/${view.course.slug}/lektion/${firstLessonNumber}`} className="button button--large">
                    <PlayIcon /> Mit Lektion {pad2(firstLessonNumber)} starten
                  </Link>
                )}
                {view.materials.length > 0 && (
                  <a href="#materialien" className="button button--secondary button--large">
                    Alle Materialien
                  </a>
                )}
              </>
            )}
          </div>
          {!locked && view.stats.percent >= 100 && (
            <p className="hero__done">
              <CheckIcon /> Du hast diesen Kurs abgeschlossen.
            </p>
          )}
        </div>
      </section>

      {locked && (
        <section className="notice">
          <LockIcon />
          <div>
            <strong>Vorschau.</strong> {view.modules.flatMap((m) => m.lessons).some((l) => l.preview) ? 'Lektionen mit „Vorschau“ kannst du sofort ansehen – der Rest wird nach dem Kauf freigeschaltet.' : 'Der Inhalt wird nach dem Kauf freigeschaltet.'}
          </div>
        </section>
      )}

      {view.modules.map((m, i) => (
        <ModuleSection key={m.id} module={m} index={i} course={view} />
      ))}

      {view.materials.length > 0 && (
        <section className="section" id="materialien">
          <div className="section__head">
            <Kicker>Materialien</Kicker>
            <h2>Alles zum Kurs</h2>
          </div>
          <ul className="materials">
            {view.materials.map((m) => (
              <li key={m.id}>
                <a href={m.url} target={m.kind === 'link' ? '_blank' : undefined} rel="noopener" className="material">
                  <span className="material__kind">{m.kind === 'file' ? 'PDF' : 'Link'}</span>
                  <span className="material__body">
                    <strong>{m.title}</strong>
                    {m.description && <span className="muted small">{m.description}</span>}
                  </span>
                  <span className="material__action">{m.kind === 'file' ? 'Herunterladen' : 'Öffnen ↗'}</span>
                </a>
              </li>
            ))}
          </ul>
        </section>
      )}

      {openLesson && (
        <LessonOverlay
          slug={view.course.slug}
          number={openLesson.number}
          onClose={() => navigate(`/kurs/${view.course.slug}`)}
          onNavigate={(n) => navigate(`/kurs/${view.course.slug}/lektion/${n}`)}
          onProgress={reload}
        />
      )}
    </main>
  );
}

function ModuleSection({ module: m, index, course }: { module: ModuleView; index: number; course: CourseView }) {
  const first = m.lessons[0]?.number;
  const last = m.lessons[m.lessons.length - 1]?.number;
  const range = first && last ? (first === last ? `Lektion ${pad2(first)}` : `Lektionen ${pad2(first)}–${pad2(last)}`) : `Modul ${index + 1}`;
  return (
    <section className={`section module${m.locked ? ' module--locked' : ''}`}>
      <div className="section__head section__head--module">
        <div>
          <Kicker>{range}</Kicker>
          <h2>{m.title}</h2>
          {m.subtitle && <p className="muted">{m.subtitle}</p>}
        </div>
        <div className="module__status">
          {m.locked && course.access === 'full' && m.releaseAt ? (
            <span className="pill pill--muted">
              <LockIcon /> Frei ab {formatDate(m.releaseAt)}
            </span>
          ) : course.access === 'full' ? (
            <>
              <ProgressBar percent={m.percent} thin className="module__bar" />
              <span className="small muted">
                {m.lessons.filter((l) => l.progress?.completed).length} von {m.lessons.length}
              </span>
            </>
          ) : null}
        </div>
      </div>
      <div className="grid grid--lessons">
        {m.lessons.map((l) => (
          <LessonCard key={l.id} lesson={l} slug={course.course.slug} />
        ))}
      </div>
    </section>
  );
}

function LessonCard({ lesson: l, slug }: { lesson: LessonCardData; slug: string }) {
  const done = l.progress?.completed ?? false;
  const started = !done && (l.progress?.percent ?? 0) > 0;
  const inner = (
    <>
      <Thumb src={l.posterUrl} seed={l.number}>
        <span className="lesson-card__number">{pad2(l.number)}</span>
        <span className="lesson-card__short">{l.shortTitle ?? l.title}</span>
        {l.durationSeconds !== null && l.durationSeconds > 0 && <span className="lesson-card__duration">{formatClock(l.durationSeconds)}</span>}
        {done && (
          <span className="lesson-card__state lesson-card__state--done">
            <CheckIcon />
          </span>
        )}
        {l.locked && (
          <span className="lesson-card__state">
            <LockIcon />
          </span>
        )}
        {l.preview && l.locked === false && !l.progress && <span className="thumb__badge thumb__badge--free">Vorschau</span>}
        {started && l.progress && <ProgressBar percent={l.progress.percent} thin className="lesson-card__bar" />}
      </Thumb>
      <div className="lesson-card__body">
        <p className="lesson-card__meta">
          <span className="accent">Lektion {pad2(l.number)}</span>
          {l.speakers && <span className="muted"> · {l.speakers}</span>}
        </p>
        <h3 className="lesson-card__title">{l.title}</h3>
        {l.excerpt && <p className="muted small lesson-card__excerpt">{l.excerpt}</p>}
        <p className="lesson-card__foot">
          {l.chapterCount > 0 && <span className="accent small">{plural(l.chapterCount, 'Learning', 'Learnings')} im Workbook</span>}
          {l.chapterCount > 0 && l.materialCount > 0 && <span className="muted small"> · </span>}
          {l.materialCount > 0 && <span className="muted small">{plural(l.materialCount, 'Material', 'Materialien')}</span>}
        </p>
      </div>
    </>
  );
  if (l.locked) {
    return <div className="lesson-card lesson-card--locked">{inner}</div>;
  }
  return (
    <Link href={`/kurs/${slug}/lektion/${l.number}`} className={`lesson-card${done ? ' lesson-card--done' : ''}`}>
      {inner}
    </Link>
  );
}
